import { useMemo } from "react";
import { cn } from "../../lib/utils";
import { CenteredSpinner } from "../CenteredSpinner";
import { DynamicDataTableBody } from "./DynamicDataTableBody";
import { DynamicDataTableHeader } from "./DynamicDataTableHeader";
import { ExternalFacetValuesProvider } from "./ExternalFacetValuesContext";
import { type RowInteraction } from "./types";
import {
	type UseServerDynamicDataTableStateParams,
	useServerDynamicDataTableState,
} from "./useServerDynamicDataTableState";

export type { UseServerDynamicDataTableStateParams as ServerDynamicDataTableStateParams } from "./useServerDynamicDataTableState";

export type ServerDynamicDataTableProps<TData extends object> = Pick<
	UseServerDynamicDataTableStateParams<TData>,
	| "name"
	| "columns"
	| "rows"
	| "totalCount"
	| "columnVisibility"
	| "defaultColumnFilters"
	| "enforcedColumnFilters"
	| "initialSorting"
	| "onFiltersChange"
	| "onSortingChange"
	| "onLoadMore"
	| "isLoadingNextPage"
> &
	RowInteraction<TData> & {
		/** Supply for server filtering so options are not limited to the currently loaded page. */
		facetValues?: Record<string, string[]>;
		dataLoading?: boolean;
		tableClassName?: string;
		minHeight?: number;
		maxHeight?: number;
		expandToContent?: boolean;
	};

export const ServerDynamicDataTable = <TData extends object>({
	name,
	columns,
	rows,
	totalCount,
	facetValues,
	columnVisibility,
	defaultColumnFilters,
	enforcedColumnFilters,
	initialSorting,
	onFiltersChange,
	onSortingChange,
	onLoadMore,
	isLoadingNextPage,
	dataLoading,
	tableClassName,
	minHeight,
	maxHeight,
	expandToContent = false,
	...rowInteraction
}: ServerDynamicDataTableProps<TData>) => {
	const {
		table,
		rows: tableRows,
		rowVirtualizer,
		parentRef,
		fitColumnWidthsToContainer,
	} = useServerDynamicDataTableState({
		name,
		columns,
		rows,
		totalCount,
		columnVisibility,
		defaultColumnFilters,
		enforcedColumnFilters,
		initialSorting,
		onFiltersChange,
		onSortingChange,
		onLoadMore,
		isLoadingNextPage,
	});

	const externalFacetMap = useMemo(() => {
		if (!facetValues) return null;
		const map = new Map<string, Map<string, number>>();
		for (const [colId, values] of Object.entries(facetValues)) {
			const inner = new Map<string, number>();
			for (const v of values) inner.set(v, 1);
			map.set(colId, inner);
		}
		return map;
	}, [facetValues]);

	const isLoading = dataLoading ?? false;
	const loadingMoreHeight = isLoadingNextPage ? 40 : 0;

	const headerHeight = 33;
	const contentHeight =
		rowVirtualizer.getTotalSize() +
		headerHeight +
		(tableRows.length > 0 ? 12 : 90) +
		loadingMoreHeight;
	const resolvedHeight = expandToContent
		? contentHeight
		: maxHeight
			? Math.min(contentHeight, maxHeight)
			: undefined;
	const containerStyle =
		minHeight && resolvedHeight
			? { height: Math.max(minHeight, resolvedHeight) }
			: { height: resolvedHeight, minHeight };

	return (
		<ExternalFacetValuesProvider value={externalFacetMap}>
			<div
				className={cn(
					"relative w-full rounded-xl",
					!expandToContent && !maxHeight && "h-full",
					tableClassName,
				)}
				style={containerStyle}
			>
				<div className="overflow-hidden h-full w-full relative border border-bc-default rounded-xl">
					<div
						ref={parentRef}
						className="relative h-full w-full overflow-auto bg-surface-light scrollbar-med"
					>
						<div
							className="w-full"
							style={{
								height: `${rowVirtualizer.getTotalSize() + headerHeight + loadingMoreHeight}px`,
							}}
						>
							<DynamicDataTableHeader
								headerGroups={table.getHeaderGroups()}
								onDoubleClick={fitColumnWidthsToContainer}
							/>
							{isLoading ? (
								<div
									data-testid="server-table-initial-loading"
									className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full"
								>
									<CenteredSpinner />
								</div>
							) : tableRows.length === 0 ? (
								<div
									data-testid="server-table-no-results"
									className="mt-[32px] w-full text-center text-tertiary italic text-[13px]"
								>
									No results found
								</div>
							) : (
								<>
									<DynamicDataTableBody
										virtualRows={rowVirtualizer.getVirtualItems()}
										rows={tableRows}
										width={table.getTotalSize()}
										{...rowInteraction}
									/>
									{isLoadingNextPage && (
										<div
											data-testid="server-table-loading-more"
											className="absolute flex justify-center items-center w-full py-3"
											style={{ top: `${rowVirtualizer.getTotalSize() + headerHeight}px` }}
										>
											<CenteredSpinner />
										</div>
									)}
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</ExternalFacetValuesProvider>
	);
};
